import type { ReactElement } from 'react';

import type { ValueFormatter } from './alternative';
import { labelIndexes, type Domain } from './geometry';
import { useTokens } from './seriesPalette';

/**
 * Cadre cartesien commun aux courbes et aux barres : grille horizontale,
 * graduations de l'axe vertical, etiquettes de l'axe horizontal.
 *
 * Le texte est dessine a **12 px** (la maquette dit 11) : c'est de l'information
 * lue, pas un ornement, et `textMuted` y tient 4,5:1 sur la carte dans les deux
 * themes. La grille, elle, est un separateur : `border`, sans pretention de
 * contraste.
 */

const FONT_SIZE = 12;
/** Largeur moyenne d'un caractere de 12 px, majoree : mieux vaut une marge de trop. */
const CHAR_WIDTH = 7;

export interface PlotFrame {
  readonly width: number;
  readonly height: number;
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly plotWidth: number;
  readonly plotHeight: number;
}

/** Marges d'un graphique : la gauche s'adapte a la plus large graduation. */
export const plotFrame = (width: number, height: number, domain: Domain, format: ValueFormatter): PlotFrame => {
  const widest = Math.max(...domain.ticks.map((tick) => format(tick).length));
  const left = Math.ceil(widest * CHAR_WIDTH) + 12;
  const top = 8;
  const right = 8;
  const bottom = 28;

  return { width, height, left, top, right, bottom, plotWidth: Math.max(width - left - right, 1), plotHeight: Math.max(height - top - bottom, 1) };
};

export interface XLabel {
  /** Abscisse du centre de l'etiquette, en pixels. */
  readonly x: number;
  readonly text: string;
}

/** Etiquettes d'abscisse a ecrire : assez espacees pour ne jamais se chevaucher. */
export const pickXLabels = (frame: PlotFrame, categories: readonly string[], xAt: (index: number) => number): XLabel[] => {
  const longest = Math.max(1, ...categories.map((category) => category.length));
  const maxLabels = Math.max(2, Math.floor(frame.plotWidth / (longest * CHAR_WIDTH + 16)));

  return labelIndexes(categories.length, maxLabels).map((index) => ({ x: xAt(index), text: categories[index] ?? '' }));
};

export interface AxesProps {
  readonly frame: PlotFrame;
  readonly domain: Domain;
  readonly yAt: (value: number) => number;
  readonly format: ValueFormatter;
  readonly xLabels: readonly XLabel[];
}

export const Axes = ({ frame, domain, yAt, format, xLabels }: AxesProps): ReactElement => {
  const tokens = useTokens();
  const right = frame.left + frame.plotWidth;

  return (
    <g fontSize={FONT_SIZE} fill={tokens.textMuted} style={{ fontFamily: 'inherit' }}>
      {domain.ticks.map((tick, rank) => (
        <g key={rank}>
          <line x1={frame.left} x2={right} y1={yAt(tick)} y2={yAt(tick)} stroke={tokens.border} strokeWidth={1} />
          <text x={frame.left - 8} y={yAt(tick)} textAnchor="end" dominantBaseline="central">
            {format(tick)}
          </text>
        </g>
      ))}
      {xLabels.map((label, rank) => {
        const half = (label.text.length * CHAR_WIDTH) / 2;
        const anchor = label.x - half < 0 ? 'start' : label.x + half > frame.width ? 'end' : 'middle';

        return (
          <text key={rank} x={label.x} y={frame.top + frame.plotHeight + 18} textAnchor={anchor}>
            {label.text}
          </text>
        );
      })}
    </g>
  );
};
